import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Palette, LinkIcon, Link2Off, Loader2 } from 'lucide-react';
import { 
  getCanvaAuthorization, 
  revokeCanvaAuthorization, 
  checkForAccessToken,
  getCanvaUserProfile,
  type CanvaAuthState 
} from '@/services/canva/auth';
import { useToast } from '@/hooks/use-toast';

interface CanvaConnectButtonProps {
  onAuthStateChange?: (authState: CanvaAuthState) => void;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  fullWidth?: boolean;
}

export const CanvaConnectButton: React.FC<CanvaConnectButtonProps> = ({
  onAuthStateChange,
  variant = 'outline',
  size = 'default',
  fullWidth = false,
}) => {
  const [authState, setAuthState] = useState<CanvaAuthState>({
    isAuthorized: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (onAuthStateChange) {
      onAuthStateChange(authState);
    }
  }, [authState, onAuthStateChange]);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      const { token } = await checkForAccessToken();
      
      if (token) {
        const profile = await getCanvaUserProfile();
        const newAuthState: CanvaAuthState = {
          isAuthorized: true,
          token,
          displayName: profile.displayName,
          userId: profile.userId,
        };
        setAuthState(newAuthState);
      } else {
        setAuthState({ isAuthorized: false });
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setAuthState({ isAuthorized: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      const token = await getCanvaAuthorization();
      
      if (token) {
        const profile = await getCanvaUserProfile();
        const newAuthState: CanvaAuthState = {
          isAuthorized: true,
          token,
          displayName: profile.displayName,
          userId: profile.userId,
        };
        setAuthState(newAuthState);
        
        toast({
          title: 'Connected to Canva',
          description: profile.displayName 
            ? `Successfully connected as ${profile.displayName}`
            : 'Successfully connected to your Canva account',
        });
      } else {
        setAuthState({ isAuthorized: false });
      }
    } catch (error) {
      console.error('Authorization failed:', error);
      setAuthState({ isAuthorized: false });
      toast({
        title: 'Connection Failed',
        description: 'Failed to connect to Canva. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleRevoke = async () => {
    try {
      setIsLoading(true);
      const success = await revokeCanvaAuthorization();
      
      if (success) {
        setAuthState({ isAuthorized: false });
        toast({
          title: 'Disconnected from Canva',
          description: 'Your Canva integration has been disconnected',
        });
      } else {
        toast({
          title: 'Disconnection Failed',
          description: 'Failed to disconnect from Canva. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Revocation failed:', error);
      toast({
        title: 'Disconnection Failed',
        description: 'An error occurred while disconnecting from Canva.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Button variant={variant} size={size} disabled className={fullWidth ? 'w-full' : ''}>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Loading...
      </Button>
    );
  }

  if (authState.isAuthorized) {
    return (
      <div className={fullWidth ? 'w-full space-y-2' : 'inline-flex items-center gap-2'}>
        <Alert className="py-2">
          <AlertDescription className="flex items-center gap-2 text-sm">
            <LinkIcon className="h-4 w-4" />
            Connected to Canva {authState.displayName ? `(${authState.displayName})` : ''}
            <span className="text-muted-foreground">• Shared team account</span>
          </AlertDescription>
        </Alert>
        <Button 
          variant="destructive"
          size={size}
          onClick={handleRevoke}
          className={fullWidth ? 'w-full' : ''}
        >
          <Link2Off className="h-4 w-4 mr-2" />
          Disconnect from Canva
        </Button>
      </div>
    );
  }

  return (
    <Button 
      variant={variant}
      size={size}
      onClick={handleConnect}
      disabled={isConnecting}
      className={fullWidth ? 'w-full' : ''}
    >
      {isConnecting ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Connecting...
        </>
      ) : (
        <>
          <Palette className="h-4 w-4 mr-2" />
          Connect to Canva
        </>
      )}
    </Button>
  );
};